import { BEMBlock } from 'helpers/classnames';
import {
    useContext, useState,
    type MouseEventHandler, type ReactNode,
} from 'react';
import { Button } from 'palette/Button';
import { LanguageSelector } from 'palette/LanguageSelector';
import { LANGUAGE_SELECT_MODAL_ID } from 'palette/LanguageSelector/constants';
import { Languages } from 'palette/LanguageSelector/types';
import { ModalContext } from 'palette/Modal';
import { Banner } from 'palette/Promobar/types';
import type { PromobarSlideItem } from 'palette/PromoBarV2/types';
import { TextLink } from 'palette/TextLink';
import { PromoBarSelector } from './components/PromoBarSelector';
import { useHeaderTopContext, HeaderTopContextProvider } from './context';

import './styles/HeaderTop.scss';

interface WrappedHeaderTopProps extends HeaderTopProps {
    banners: Banner[];
    defaultId: number;
    promoBarSlides: PromobarSlideItem[] | null;
}

interface HeaderTopProps {
    uspText: string;
    languages: Languages;
    activeStore: string;
    activeLanguage: string;
    customerServiceLink: { url: string; label: string };
    languageSelectorTitle: string;
    languageSelectorSubTitle: string;
    onPreviousBannerClick: () => void;
    onNextBannerClick: () => void;
    storeSelector?: ReactNode;
    onMainLinkClick: MouseEventHandler<HTMLElement>;
    onExpandedLinkClick: MouseEventHandler<HTMLElement>;
    ariaLabel?: {
        languageSelectButton: string;
        leftArrowButton: string;
        rightArrowButton: string;
    };
}

function HeaderTop({
    customerServiceLink,
    uspText,
    languages,
    activeStore,
    activeLanguage,
    languageSelectorTitle,
    languageSelectorSubTitle,
    onPreviousBannerClick,
    onNextBannerClick,
    storeSelector = null,
    onMainLinkClick,
    onExpandedLinkClick,
    ariaLabel,
}: HeaderTopProps) {
    const [isPromoBarOpen, setIsPromoBarOpen] = useState(false);
    const {
        activeSlide, activeBanner, isExpanded, startInterval, stopInterval,
        isLanguageSelectorActive,
    } = useHeaderTopContext();
    const { setActiveModalId } = useContext(ModalContext);
    const isActive = isLanguageSelectorActive || isPromoBarOpen;
    const [block, BEMElement] = BEMBlock('palette-header-top', { active: isActive });
    const showLanguageSelector = languages?.[activeStore]?.length > 1;

    const promoBarStyle = activeSlide ? {
        backgroundColor: activeSlide.background,
        color: activeSlide.color,
    } : {
        backgroundColor: activeBanner?.backgroundColor,
        color: activeBanner?.fontColor,
    };

    const handleLanguageButtonClick = () => {
        setActiveModalId(currentState => (currentState === LANGUAGE_SELECT_MODAL_ID ? null : LANGUAGE_SELECT_MODAL_ID));
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <div
                className={block}
                style={promoBarStyle}
                {...!isExpanded && {
                    onBlur: startInterval,
                    onFocus: stopInterval,
                    onMouseOut: startInterval,
                    onMouseOver: stopInterval,
                }}
            >
                <div className={BEMElement('left-side-section')}>
                    {storeSelector}
                    {showLanguageSelector ? (
                        <>
                            <div className={BEMElement('divider')} />
                            <Button
                                isTransparent
                                ariaLabel={ariaLabel?.languageSelectButton}
                                iconColor={promoBarStyle.color}
                                iconName="palette_globe"
                                onClick={handleLanguageButtonClick}
                            />
                        </>
                    ) : null}
                </div>
                <PromoBarSelector
                    ariaLabel={ariaLabel}
                    isV2={!!activeSlide}
                    setIsPromoBarOpen={setIsPromoBarOpen}
                    uspText={uspText}
                    onExpandedLinkClick={onExpandedLinkClick}
                    onMainLinkClick={onMainLinkClick}
                    onNextBannerClick={onNextBannerClick}
                    onPreviousBannerClick={onPreviousBannerClick}
                />
                <div className={BEMElement('right-side-section')}>
                    <TextLink
                        isUnderlinedOnHover
                        href={customerServiceLink.url}
                    >
                        {customerServiceLink.label}
                    </TextLink>
                </div>
            </div>
            {showLanguageSelector ? (
                <LanguageSelector
                    activeLanguage={activeLanguage}
                    className={BEMElement('language-selector')}
                    languages={languages}
                    modalId={LANGUAGE_SELECT_MODAL_ID}
                    store={activeStore}
                    subTitle={languageSelectorSubTitle}
                    title={languageSelectorTitle}
                />
            ) : null}
        </div>
    );
}

export default function WrappedHeaderTop({
    banners,
    defaultId,
    promoBarSlides,
    customerServiceLink,
    uspText,
    languages,
    activeStore,
    activeLanguage,
    languageSelectorSubTitle,
    languageSelectorTitle,
    onPreviousBannerClick,
    onNextBannerClick,
    storeSelector,
    onMainLinkClick,
    onExpandedLinkClick,
    ariaLabel,
}: WrappedHeaderTopProps) {
    return (
        <HeaderTopContextProvider
            banners={banners}
            defaultId={defaultId}
            promoBarSlides={promoBarSlides}
        >
            <HeaderTop
                activeLanguage={activeLanguage}
                activeStore={activeStore}
                ariaLabel={ariaLabel}
                customerServiceLink={customerServiceLink}
                languages={languages}
                languageSelectorSubTitle={languageSelectorSubTitle}
                languageSelectorTitle={languageSelectorTitle}
                storeSelector={storeSelector}
                uspText={uspText}
                onExpandedLinkClick={onExpandedLinkClick}
                onMainLinkClick={onMainLinkClick}
                onNextBannerClick={onNextBannerClick}
                onPreviousBannerClick={onPreviousBannerClick}
            />
        </HeaderTopContextProvider>
    );
}
